import React, { useEffect, useMemo, useState } from 'react'
import {
	Autocomplete,
	Box,
	Chip,
	CircularProgress,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from '@mui/material'
import { AppCardTitle, AppEmptyLine, AppResultsSkeleton, AppNote } from '@components'
import { dsColorNew, dataColors } from '@palette'

/**
 * Sprint 2 · point 4 — Revenue Sources.
 *
 * Item-level revenue by service_name. Each row shows which bucket the
 * item belongs to (from Extras Buckets admin) so RM can spot
 * classification gaps ('Unassigned' rows) and validate the auto-map.
 *
 *   <RevenueSourcesTable from="2026-01-01" to="2026-06-11"
 *                        offices={['A1']} categories={['CDMR']}
 *                        compare="py"/>
 */
const fmtNum = (n) => new Intl.NumberFormat('es-ES').format(n)
const fmtMoney = (n) =>
	new Intl.NumberFormat('es-ES', {
		style: 'currency', currency: 'EUR', maximumFractionDigits: 0,
	}).format(n)
const fmtPct = (n) =>
	new Intl.NumberFormat('es-ES', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
		.format(n) + '%'

const BUCKET_COLORS = {
	tarifa_base:          dsColorNew.webBlue500,
	cargos_operativos:    dsColorNew.tableHead,
	ingresos_auxiliares:  dsColorNew.cyan,
	recuperacion_costes:  dataColors.violet500,
	unassigned:           dsColorNew.warningBar,
}

export default function RevenueSourcesTable({
	from, to, offices = [], categories = [], compare = 'py',
}) {
	const [data, setData]       = useState(null)
	const [error, setError]     = useState(null)
	const [loading, setLoading] = useState(true)
	const [filter, setFilter]   = useState([])

	useEffect(() => {
		if (!from || !to) return
		setLoading(true); setError(null)
		const qs = new URLSearchParams({ from, to })
		if (offices.length)    qs.set('offices', offices.join(','))
		if (categories.length) qs.set('sipps',   categories.join(','))
		if (compare && compare !== 'none') qs.set('compare', compare)
		fetch(`/api/my-business/revenue/sources?${qs}`, { credentials: 'include' })
			.then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
			.then(setData)
			.catch(e => setError(e.message))
			.finally(() => setLoading(false))
	}, [from, to, offices.join(','), categories.join(','), compare])

	// Hooks MUST run before any early return so their call order stays
	// stable between the loading render and the ready render — Rule of
	// Hooks. `rows` is derived from `data` so it tolerates a null.
	const rows = data?.rows || []
	// Unique values for the Autocomplete dropdown (Thibault feedback #2):
	// operator wants a picker with unique options + free-typing, not a
	// plain text field.
	const options = useMemo(() => {
		const names = new Set()
		const buckets = new Set()
		rows.forEach(r => {
			if (r.service_name) names.add(r.service_name)
			if (r.bucket_name) buckets.add(r.bucket_name)
		})
		return [
			...[...names].sort().map(v => ({ group: 'Service', label: v })),
			...[...buckets].sort().map(v => ({ group: 'Bucket',  label: v })),
		]
	}, [rows])

	if (error) return (
		<Paper sx={{ p: 2 }}>
			<AppNote severity="error">Revenue sources unreachable: {error}</AppNote>
		</Paper>
	)
	// Loading (also a refetch): the card's skeleton, so nothing can be clicked half-loaded
	if (loading || !data) return <AppResultsSkeleton rows={6}/>
	if (!rows.length) return (
		<Paper sx={{ p: 3 }}>
			<AppEmptyLine>No item-level revenue in this range.</AppEmptyLine>
		</Paper>
	)

	const selected = Array.isArray(filter) ? filter : []
	const visible = selected.length === 0
		? rows
		: rows.filter(r => selected.some(sel =>
			(sel.group === 'Service' && r.service_name === sel.label) ||
			(sel.group === 'Bucket'  && r.bucket_name  === sel.label)
		))
	const showPY = data.rows[0]?.revenue_py !== undefined

	return (
		<Paper sx={{ p: 2.5 }}>
			<Box display="flex" alignItems="center" justifyContent="space-between"
			     sx={{ mb: 1.5 }} flexWrap="wrap" gap={1}>
				<AppCardTitle icon="coins" title={`Revenue Sources · ${rows.length} distinct items · ${fmtMoney(data.total_revenue)}`}/>
				<Autocomplete multiple size="small"
				              options={options}
				              value={selected}
				              groupBy={(o) => o.group}
				              getOptionLabel={(o) => o.label}
				              isOptionEqualToValue={(a, b) =>
				                  a.group === b.group && a.label === b.label}
				              onChange={(_, next) => setFilter(next)}
				              renderInput={(params) => (
				                  <TextField {...params}
				                             placeholder={selected.length ? '' : 'Filter by service or bucket…'}/>
				              )}
				              sx={{ minWidth: 320, maxWidth: 520 }}/>
			</Box>
			<TableContainer sx={{ maxHeight: 520 }}>
				<Table size="small" stickyHeader>
					<TableHead>
						<TableRow>
							<TableCell width={160}>Bucket</TableCell>
							<TableCell>Service</TableCell>
							<TableCell>Type</TableCell>
							<TableCell align="right" width={100}>Bookings</TableCell>
							<TableCell align="right" width={100}>Items</TableCell>
							<TableCell align="right" width={140}>Revenue</TableCell>
							<TableCell align="right" width={90}>Share</TableCell>
							{showPY && <TableCell align="right" width={130}>PY</TableCell>}
							{showPY && <TableCell align="right" width={90}>Δ%</TableCell>}
						</TableRow>
					</TableHead>
					<TableBody>
						{visible.map((r, i) => {
							const key = `${r.service_type_id ?? 'null'}-${r.service_name}-${i}`
							const color = BUCKET_COLORS[r.bucket_code] || dsColorNew.textMuted
							const delta = r.delta_pct
							const deltaColor = delta == null ? 'default'
								: delta > 0 ? 'success'
								: delta < 0 ? 'error' : 'default'
							return (
								<TableRow key={key}
								          hover
								          sx={{ bgcolor: r.bucket_code === 'unassigned'
								                        ? 'warning.50' : undefined }}>
									<TableCell>
										<Chip size="small" label={r.bucket_name}
										      sx={{ bgcolor: color, color: dsColorNew.white,
										            fontWeight: 600, fontSize: 12 }}/>
									</TableCell>
									<TableCell>{r.service_name || <em>—</em>}</TableCell>
									<TableCell>
										<Typography component="span" sx={{ fontSize: 13, color: dsColorNew.textMuted }}>
											{r.service_type || (r.service_type_id
												? `type#${r.service_type_id}`
												: <em>none</em>)}
										</Typography>
									</TableCell>
									<TableCell align="right">{fmtNum(r.bookings)}</TableCell>
									<TableCell align="right">{fmtNum(r.items)}</TableCell>
									<TableCell align="right"><b>{fmtMoney(r.revenue)}</b></TableCell>
									<TableCell align="right">{fmtPct(r.share_pct)}</TableCell>
									{showPY && (
										<TableCell align="right">
											{fmtMoney(r.revenue_py || 0)}
										</TableCell>
									)}
									{showPY && (
										<TableCell align="right">
											{delta == null ? <em>—</em> : (
												<Chip size="small" color={deltaColor}
												      variant="filled"
												      label={`${delta > 0 ? '+' : ''}${delta}%`}
												      sx={{ fontWeight: 700, fontSize: 12 }}/>
											)}
										</TableCell>
									)}
								</TableRow>
							)
						})}
					</TableBody>
				</Table>
			</TableContainer>
		</Paper>
	)
}
